import { useState, useEffect } from "react";
// material
import { Box, Card, Container, Typography } from "@mui/material";
// components
import Scrollbar from "../table/Scrollbar";
import ListToolbar from "../table/ListToolbar";
import TableView from "../Table";
import {
  parseCurrency,
  parseDateTime,
  getMonday,
  formatDateFilter,
} from "../../utils/tableFunctions";
import {
  getChargebackFilterParam,
  getChargebackList,
  getChargebackCSV,
} from "../../services/reconciliations";
import moment from "moment";
import { Log, getNextPage } from "../../services/services";

import {
  DateRangeType,
  InputType,
  MinMaxType,
  MultiCheckboxesType,
  MultiSelect,
} from "../table/Filter";
import HeaderHelpPage from "../HeaderHelpPage";
import CircularLoader from "../circularLoader";

// ----------------------------------------------------------------------

export default function ChargebackList() {
  const [chargeback, setChargeback] = useState();
  const [next, setNext] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [tableData, setTableData] = useState();
  const [selected, setSelected] = useState([]);
  const [filterName, setFilterName] = useState();
  const [fParams, setFParams] = useState({});

  // ------------------ filterStuffies --------------------//
  const [fTransaction, setFTransaction] = useState("");
  const [fMerchantName, setFMerchantName] = useState([]);
  const [fTerminal, setFTerminal] = useState("");
  const [fMethod, setFMethod] = useState([]);
  const [fDate, setFDate] = useState([getMonday(), moment()]);
  const [fBrand, setFBrand] = useState([]);
  const [fCurrency, setFCurrency] = useState([]);
  const [ftransactionAmt, setFTransactionAmt] = useState([]);
  const [fBaseAmt, setFBaseAmt] = useState([]);
  const [reset, setReset] = useState(false);
  const filter = {
    transaction_id: fTransaction,
    terminal_id: fTerminal,
    merchant_name_list: fMerchantName,
    start_datetime: formatDateFilter(fDate[0]),
    end_datetime: formatDateFilter(fDate[1]),
    payment_brand_list: fBrand,
    payment_method_list: fMethod,
    request_currency_list: fCurrency,
    min_request_amount: fBaseAmt[0],
    max_request_amount: fBaseAmt[1],
    transaction_currency_list: "",
    min_transaction_amount: ftransactionAmt[0],
    max_transaction_amount: ftransactionAmt[1],
  };

  const filterComponents = () => {
    return (
      fParams &&
      fParams.merchant_name && (
        <Container sx={{ overflowX: "auto" }}>
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ my: 1 }}>
              Transaction ID
            </Typography>
            <InputType
              value={fTransaction}
              setFInput={setFTransaction}
              reset={reset}
            />
            <Typography variant="subtitle1" sx={{ my: 1 }}>
              Terminal ID
            </Typography>
            <InputType
              value={fTerminal}
              setFInput={setFTerminal}
              reset={reset}
            />
            <Box sx={{ height: 15 }} />
            <Typography variant="subtitle1" sx={{ my: 1 }}>
              Merchant Name
            </Typography>
            <MultiSelect
              options={fParams.merchant_name.map((name) => ({
                value: name ? name : "",
                label: name ? name : "",
              }))}
              setSelectedValues={setFMerchantName}
              selectedValues={fMerchantName}
              reset={reset}
            />
            <Box sx={{ height: 15 }} />
            <Typography variant="subtitle1" sx={{ my: 1 }}>
              Date
            </Typography>
            <DateRangeType date={fDate} setDate={setFDate} reset={reset} />
            <Box sx={{ height: 15 }} />
            <Typography variant="subtitle1" sx={{ my: 1 }}>
              Payment Brand
            </Typography>
            <MultiCheckboxesType
              items={fParams.payment_brand}
              checkedValues={fBrand}
              setCheckedValues={setFBrand}
              reset={reset}
            />
            <Box sx={{ height: 15 }} />
            <Typography variant="subtitle1" sx={{ my: 1 }}>
              Payment Method
            </Typography>
            <MultiCheckboxesType
              items={fParams.payment_method}
              checkedValues={fMethod}
              setCheckedValues={setFMethod}
              reset={reset}
            />
            <Box sx={{ height: 15 }} />
            <Typography variant="subtitle1" sx={{ my: 1 }}>
              Base Currency
            </Typography>
            <MultiCheckboxesType
              items={fParams.base_currency}
              checkedValues={fCurrency}
              setCheckedValues={setFCurrency}
              reset={reset}
            />
            <Box sx={{ height: 15 }} />
            <Typography variant="subtitle1" sx={{ my: 1 }}>
              Base Amount
            </Typography>
            <MinMaxType
              minVal={fParams.min_base_amount}
              maxVal={fParams.max_base_amount}
              setValue={setFBaseAmt}
              value={fBaseAmt}
              reset={reset}
            />
            <Box sx={{ height: 15 }} />
            <Typography variant="subtitle1" sx={{ my: 1 }}>
              Transaction Amount
            </Typography>
            <MinMaxType
              minVal={fParams.min_transaction_amount}
              maxVal={fParams.max_transaction_amount}
              setValue={setFTransactionAmt}
              value={ftransactionAmt}
              reset={reset}
            />
            <Box sx={{ height: 15 }} />
          </Box>
        </Container>
      )
    );
  };

  const onFilterSubmit = () => {
    Log.info('Click button [Apply Filter] - Search Chargeback list', filter);
    getChargebackList(setChargeback, null, false, filter);
  };

  // ------------------ end of filter stuffies --------------------//

  useEffect(() => {
    if (reset) setReset(false);
  }, [reset]);

  const getCSVData = async () => {
    const data = await getChargebackCSV(filter);
    return data;
  };

  const getNext = async () => {
    if (!next) return;
    const res = await getNextPage({
      url: next,
      filter: filter,
    });
    let newRows = formatData(res);
    setTableData((old) => [...old, ...newRows]);
    setNext(res.next ? res.next : false);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  useEffect(() => {
    if (!loaded) {
      getChargebackFilterParam(setFParams);
      getChargebackList(setChargeback, setLoaded, false, filter);
    }
  }, [loaded]);

  const formatData = (input) => {
    let tableData = [];
    input.results.forEach(
      ({
        id,
        transaction,
        acquirer_request_amount,
        acquirer_request_currency,
        merchant,
        payment_brand,
        terminal,
        transaction_amount,
        transaction_currency,
        transaction_datetime,
        transaction_state,
        transaction_type,
      }) => {
        tableData.push({
          id: transaction,
          transaction_ID: transaction,
          terminal_ID: terminal && terminal.terminal_id,
          merchant: merchant && merchant.name,
          transaction_time:
            transaction_datetime && parseDateTime(transaction_datetime),
          brand: payment_brand && payment_brand.name,
          request_currency:
            acquirer_request_currency && acquirer_request_currency,
          request_amount:
            acquirer_request_amount && parseCurrency(acquirer_request_amount),
          transaction_currency: transaction_currency && transaction_currency,
          transaction_amount:
            transaction_amount && parseCurrency(transaction_amount),
          type: transaction_type && transaction_type.transaction_type,
          state: transaction_state && transaction_state.transaction_state,
        });
      }
    );
    return tableData;
  };

  useEffect(() => {
    if (chargeback) {
      setTableData(formatData(chargeback));
      setNext(chargeback.next);
    }
  }, [chargeback]);

  return (
    <Container>
      <HeaderHelpPage title="Chargeback" description="This page’s purpose is to review chargeback transaction from Acquirers" />
      {loaded ? (
        <Card>
          <ListToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
            date={fDate}
            filter={true}
            loaded={loaded}
            type="Chargeback_transactions"
            filterFunction={filterComponents}
            onFilterSubmit={onFilterSubmit}
            setReset={setReset}
            csv={false}
            getCSVData={chargeback.count > 0 ? getCSVData : false}
          />
          <Scrollbar>
            <TableView
              rows={tableData}
              link={`/3/app/details/chargeback`}
              getNext={getNext}
              count={chargeback.count}
              query={filterName}
            />
          </Scrollbar>
        </Card>
      ) : (
        <CircularLoader />
      )}
    </Container>
  );
}
